/**
 * Glassmorphism Streak Hero — UI Wave B.
 * Replaces the plain streak number with a screenshot-worthy glass card:
 *   • Semi-transparent bg + 16-20px backdrop blur
 *   • Subtle inner streak-gradient (amber → orange → rose)
 *   • Animated flame + SVG ring (reuses AnimatedStreak internals)
 *   • Hover lift, light + dark variants from design tokens
 *
 * Props:
 *   stats: { current_streak, weekly_freeze_available, ... } — dashboard streak object
 *   onUseFreeze: () => void — opens the freeze confirm dialog
 *   compact: bool — when true, used in sidebar slot; otherwise full hero
 */
import { useEffect, useRef, useState } from "react";
import AnimatedStreak from "./AnimatedStreak";
import ShareButtons from "./ShareButtons";
import { api } from "@/lib/api";
import { Snowflake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { webAppUrl } from "@/lib/platform";

export default function GlassmorphismStreakHero({ stats, onUseFreeze, compact = false }) {
  const streak = stats?.streak || stats || {};
  const count = streak.current_streak || 0;
  const todayCompleted = !!streak.completed_today;
  const freezeAvailable = !!streak.weekly_freeze_available;
  const longest = streak.longest_streak || count;
  const rootRef = useRef(null);
  const [flashing, setFlashing] = useState(false);

  // Deep-link target — Sunday push notifications land at /student#streak-share
  // and we scroll the hero into view + briefly flash it for visual continuity.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handle = () => {
      if (window.location.hash === "#streak-share" && rootRef.current) {
        rootRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlashing(true);
        const t = setTimeout(() => setFlashing(false), 1800);
        return () => clearTimeout(t);
      }
      return undefined;
    };
    const cleanup = handle();
    window.addEventListener("hashchange", handle);
    return () => {
      window.removeEventListener("hashchange", handle);
      if (cleanup) cleanup();
    };
  }, []);

  return (
    <div
      ref={rootRef}
      id="streak-share"
      className={`relative overflow-hidden rounded-2xl glass transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl ${
        compact ? "p-4" : "p-5 sm:p-6"
      } ${flashing ? "ring-4 ring-amber-400 ring-offset-2 scale-[1.02]" : ""}`}
      data-testid="glass-streak-hero"
    >
      {/* Inner gradient wash (pointer-events disabled so clicks pass through) */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          backgroundImage:
            "radial-gradient(circle at 0% 100%, rgba(245, 158, 11, 0.22) 0, transparent 55%), radial-gradient(circle at 100% 0%, rgba(225, 29, 72, 0.18) 0, transparent 55%)",
        }}
      />

      <div className="relative flex items-center gap-4">
        <AnimatedStreak count={count} todayCompleted={todayCompleted} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-text-secondary">
            {todayCompleted ? "Today secured" : "Don't break the chain"}
          </div>
          <div className="text-sm text-text-primary font-medium mt-0.5">
            {count === 0 ? (
              <>Start a streak today — just one card counts</>
            ) : (
              <>
                {count}-day streak ·{" "}
                <span className="text-text-muted">best: {longest}</span>
              </>
            )}
          </div>
        </div>

        {freezeAvailable && !todayCompleted && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="border-brand-200 text-brand-700 hover:bg-brand-50 hover:text-brand-900 hidden sm:inline-flex"
            onClick={onUseFreeze}
            data-testid="glass-streak-freeze-btn"
          >
            <Snowflake className="w-3.5 h-3.5 mr-1" />
            Use freeze
          </Button>
        )}
      </div>

      {!compact && (
        <div className="relative grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-white/40 dark:border-white/10">
          <Stat label="Best" value={longest} />
          <Stat label="Freezes" value={freezeAvailable ? "1" : "0"} sub="weekly" />
          <Stat label="Status" value={todayCompleted ? "🔒" : "🔓"} sub={todayCompleted ? "secured" : "open"} />
        </div>
      )}

      {!compact && count >= 1 && (
        <div className="relative mt-4 pt-3 border-t border-white/40 dark:border-white/10" data-testid="streak-share-row">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-[0.15em] text-text-muted font-bold">
              Show off your streak
            </span>
          </div>
          <ShareButtons
            getShareUrl={async () => {
              const r = await api.post("/student/share/streak");
              return {
                url: `${webAppUrl()}/share/streak/${r.data.share_token}`,
                newly_awarded_badge: r.data.newly_awarded_badge,
              };
            }}
            defaultText={`🔥 ${count}-day streak on PrepMaster — ${count >= 7 ? "consistency pays off" : "just getting started"}!`}
            testIdPrefix="streak-share"
          />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="text-center">
      <div className="text-base font-bold tabular-nums text-text-primary">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">{label}</div>
      {sub && <div className="text-[9px] text-text-muted">{sub}</div>}
    </div>
  );
}
